import { 
  collection, 
  addDoc, 
  updateDoc,
  doc,
  getDocs,
  getDoc,
  query,
  where,
  orderBy,
  limit,
  startAfter,
  Timestamp,
  DocumentSnapshot
} from 'firebase/firestore';
import { db } from '../config/firebase';
import type { Quote, QuoteEvent, QuoteFilters } from '../types/quotes';
import { invoiceService } from './invoiceService';

export const quoteService = {
  /**
   * Create a new quote in Firestore
   * @param quoteData Quote data without id, createdAt, updatedAt
   * @returns Quote ID
   */
  async createQuote(quoteData: Omit<Quote, 'id' | 'createdAt' | 'updatedAt'>): Promise<string> {
    const docRef = await addDoc(collection(db, 'quotes'), {
      ...quoteData,
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now()
    });
    
    // Log event
    await this.logEvent({
      quoteId: docRef.id,
      type: 'created',
      actorUserId: quoteData.createdByUserId,
      actorUserName: quoteData.createdByUserName,
      payload: { initialData: quoteData }
    });
    
    return docRef.id;
  },

  /**
   * Get quote by ID
   * @param quoteId Quote ID
   * @returns Quote or null if not found
   */
  async getQuote(quoteId: string): Promise<Quote | null> {
    const docRef = doc(db, 'quotes', quoteId);
    const docSnap = await getDoc(docRef);
    
    if (!docSnap.exists()) {
      return null;
    }
    
    const data = docSnap.data();
    return {
      id: docSnap.id,
      ...data,
      createdAt: data.createdAt?.toDate() || new Date(),
      updatedAt: data.updatedAt?.toDate() || new Date(),
      submittedAt: data.submittedAt?.toDate() || new Date(),
      completedAt: data.completedAt?.toDate(),
      fragebogenSubmittedAt: data.fragebogenSubmittedAt?.toDate()
    } as Quote;
  },

  /**
   * Get quotes for a specific customer (by userId and email)
   * Returns quotes they created OR where their email matches clientEmail
   * @param userId User ID
   * @param userEmail User email
   * @returns Array of quotes
   */
  async getCustomerQuotes(userId: string, userEmail: string): Promise<Quote[]> {
    // Query 1: Quotes created by user (has index)
    const q1 = query(
      collection(db, 'quotes'),
      where('createdByUserId', '==', userId)
    );
    
    // Query 2: Quotes where user is the customer (no orderBy to avoid index requirement)
    const q2 = query(
      collection(db, 'quotes'),
      where('clientEmail', '==', userEmail)
    );
    
    const [snapshot1, snapshot2] = await Promise.all([
      getDocs(q1),
      getDocs(q2)
    ]);
    
    // Merge and deduplicate
    const quoteMap = new Map<string, Quote>();
    
    [...snapshot1.docs, ...snapshot2.docs].forEach(docSnap => {
      if (!quoteMap.has(docSnap.id)) {
        const data = docSnap.data();
        quoteMap.set(docSnap.id, {
          id: docSnap.id,
          ...data,
          createdAt: data.createdAt?.toDate() || new Date(),
          updatedAt: data.updatedAt?.toDate() || new Date(),
          submittedAt: data.submittedAt?.toDate() || new Date(),
          completedAt: data.completedAt?.toDate(),
          cancelledAt: data.cancelledAt?.toDate(),
          fragebogenSubmittedAt: data.fragebogenSubmittedAt?.toDate()
        } as Quote);
      }
    });
    
    // Sort in memory by createdAt desc (works without index)
    return Array.from(quoteMap.values()).sort((a, b) => 
      b.createdAt.getTime() - a.createdAt.getTime()
    );
  },

  /**
   * List quotes with filters and pagination
   * @param filters Quote filters
   * @param pageSize Number of quotes per page
   * @param lastDoc Last document for pagination
   * @returns Quotes and last visible document
   */
  async listQuotes(
    filters: QuoteFilters, 
    pageSize = 25, 
    lastDoc?: DocumentSnapshot
  ): Promise<{ quotes: Quote[], lastVisible: DocumentSnapshot | null }> {
    let q = query(collection(db, 'quotes'));
    
    // Apply filters
    if (filters.status && filters.status.length > 0) {
      q = query(q, where('status', 'in', filters.status));
    }
    if (filters.createdBy) {
      q = query(q, where('createdByUserId', '==', filters.createdBy));
    }
    if (filters.dateFrom) {
      q = query(q, where('createdAt', '>=', Timestamp.fromDate(filters.dateFrom)));
    }
    if (filters.dateTo) {
      q = query(q, where('createdAt', '<=', Timestamp.fromDate(filters.dateTo)));
    }
    
    // Order and paginate
    q = query(q, orderBy('createdAt', 'desc'), limit(pageSize));
    if (lastDoc) {
      q = query(q, startAfter(lastDoc));
    }
    
    const snapshot = await getDocs(q);
    const quotes = snapshot.docs.map(docSnap => {
      const data = docSnap.data();
      return {
        id: docSnap.id,
        ...data,
        createdAt: data.createdAt?.toDate() || new Date(),
        updatedAt: data.updatedAt?.toDate() || new Date(),
        submittedAt: data.submittedAt?.toDate() || new Date(),
        completedAt: data.completedAt?.toDate(),
        fragebogenSubmittedAt: data.fragebogenSubmittedAt?.toDate()
      } as Quote;
    });
    
    const lastVisible = snapshot.docs.length > 0 
      ? snapshot.docs[snapshot.docs.length - 1] 
      : null;
    
    return { quotes, lastVisible };
  },

  /**
   * Update quote fields
   * @param quoteId Quote ID
   * @param updates Partial quote data to update
   */
  async updateQuote(quoteId: string, updates: Partial<Omit<Quote, 'id' | 'createdAt' | 'updatedAt'>>): Promise<void> {
    const docRef = doc(db, 'quotes', quoteId);
    await updateDoc(docRef, {
      ...updates,
      updatedAt: Timestamp.now()
    });
  },

  /**
   * Update quote status
   * @param quoteId Quote ID
   * @param newStatus New status
   * @param actorUserId User making the change
   * @param actorUserName User name
   */
  async updateQuoteStatus(
    quoteId: string, 
    newStatus: Quote['status'], 
    actorUserId: string,
    actorUserName: string
  ): Promise<void> {
    const docRef = doc(db, 'quotes', quoteId);
    
    // Get previous status for audit log
    const quoteSnap = await getDoc(docRef);
    const previousStatus = quoteSnap.exists() ? quoteSnap.data().status : undefined;
    
    const updates: Record<string, unknown> = {
      status: newStatus,
      updatedAt: Timestamp.now()
    };
    
    // Set completedAt timestamp when order is completed
    if (newStatus === 'completed') {
      updates.completedAt = Timestamp.now();
    }
    
    await updateDoc(docRef, updates);
    
    // Create invoice automatically when quote is completed
    if (newStatus === 'completed' && previousStatus !== 'completed') {
      try {
        // Get the updated quote data using existing function
        const quoteData = await this.getQuote(quoteId);
        if (quoteData) {
          await invoiceService.createInvoiceFromQuote(
            quoteData,
            actorUserId,
            actorUserName
          );
        }
      } catch (error) {
        // Log error but don't fail the status update
        console.error('Error creating invoice for completed quote:', error);
      }
    }
    
    // Log event
    await this.logEvent({
      quoteId,
      type: 'status_changed',
      actorUserId,
      actorUserName,
      payload: { newStatus, previousStatus }
    });
  },

  /**
   * Log event to audit trail
   * @param eventData Event data without id and createdAt
   */
  async logEvent(eventData: Omit<QuoteEvent, 'id' | 'createdAt'>): Promise<void> {
    await addDoc(collection(db, 'quote_events'), {
      ...eventData,
      createdAt: Timestamp.now()
    });
  },

  /**
   * Cancel a quote with reason tracking
   * Updates status to 'cancelled' and logs cancellation details
   * @param quoteId Quote ID
   * @param reason Reason for cancellation
   * @param actorUserId User ID who cancelled
   * @param actorUserName User name who cancelled
   */
  async cancelQuote(
    quoteId: string,
    reason: string,
    actorUserId: string,
    actorUserName: string
  ): Promise<void> {
    await this.updateQuoteStatus(quoteId, 'cancelled', actorUserId, actorUserName);
    
    await this.updateQuote(quoteId, {
      cancellationReason: reason,
      cancelledAt: new Date(),
      cancelledBy: actorUserId,
      cancelledByName: actorUserName
    });
    
    console.log(`✅ Quote ${quoteId} cancelled by ${actorUserName}`);
  },

  /**
   * Export quotes to CSV
   * @param quotes Array of quotes to export
   * @returns CSV string
   */
  exportToCSV(quotes: Quote[]): string {
    const headers = ['ID', 'Kunde', 'Betrag', 'Status', 'Eingereicht', 'Erstellt von', 'Abgeschlossen'];
    const rows = quotes.map(q => [
      q.id.substring(0, 8),
      q.clientName,
      `€${q.amountTotal.toFixed(2)}`,
      q.status,
      q.submittedAt.toLocaleDateString('de-DE'),
      q.createdByUserName,
      q.completedAt ? q.completedAt.toLocaleDateString('de-DE') : '-'
    ]);
    
    const csv = [headers, ...rows]
      .map(row => row.map(cell => `"${cell}"`).join(','))
      .join('\n');
    
    return csv;
  }
};

